import React, { useState } from 'react'
import { useConfig } from '@peracto/peracto-config'

import Select from '@peracto/peracto-editor-ui/dist/Select'
import TextInput from '@peracto/peracto-editor-ui/dist/TextInput'
import Checkbox from '@peracto/peracto-editor-ui/dist/Checkbox'
import Slider from '@peracto/peracto-editor-ui/dist/Slider'
import File from '@peracto/peracto-editor-ui/dist/File'

import {
    STATE_KEY_BACKGROUND,
    STATE_KEY_POSITION,
    STATE_KEY_BORDER,
    STATE_KEY_EFFECTS,
    STATE_KEY_ANCHOR,
    TYPE_PADDING,
    TYPE_PADDING_MOBILE,
    TYPE_MARGIN,
    TYPE_LOCKS,
    TYPE_BACKGROUND_COLOR,
    TYPE_BACKGROUND_COLOR_OPACITY,
    TYPE_BACKGROUND_IMAGE,
    TYPE_BACKGROUND_VIDEO,
    TYPE_BACKGROUND_POSITION,
    TYPE_BACKGROUND_SIZE,
    TYPE_BACKGROUND_REPEAT,
    TYPE_BACKGROUND_MODE,
    TYPE_BACKGROUND_LOADING_METHOD,
    TYPE_BORDER_X,
    TYPE_BORDER_Y,
    TYPE_BORDER_WIDTH,
    TYPE_BORDER_COLOR,
    TYPE_BORDER_STYLE,
    TYPE_ANIMATION_STYLE,
    TYPE_ANIMATION_DELAY,
    TYPE_ANIMATION_DURATION,
    TYPE_ANIMATION_HOVER,
    DIRECTION_VERT,
    DIRECTION_HORZ,
    BACKGROUND_MODE_IMAGE_FLAG,
    BACKGROUND_MODE_VIDEO_FLAG,
    TYPE_BOX_SHADOW,
    TYPE_ANCHOR_ID,
    TYPE_BACKGROUND_ALT_TEXT,
} from '../const'

import Lock from './Lock'
import Size from './Size'
import Group from './Group'

import isLocked from '../util/isLocked'
import getOpposite from '../util/getOpposite'
import getPositionsForDirection from '../util/getPositionsForDirection'

import debounce from 'lodash/debounce'

let negativeValues = []

for (let i = -200; i < 0; i += 5) {
    negativeValues.push({ label: i, value: i })
}

const BlockDecoratorForm = ({ onChange, state, options, translations }) => {
    const config = useConfig()
    const { colorOptions } = config.get('editorConfig') || []
    const themeColours = colorOptions || []

    const {
        [STATE_KEY_BACKGROUND]: backgroundDecoratorState = {
            [TYPE_BACKGROUND_MODE]: BACKGROUND_MODE_IMAGE_FLAG,
            [TYPE_BACKGROUND_COLOR]: null,
            [TYPE_BACKGROUND_COLOR_OPACITY]: 1,
            [TYPE_BACKGROUND_IMAGE]: '',
            [TYPE_BACKGROUND_VIDEO]: '',
            [TYPE_BACKGROUND_POSITION]: null,
            [TYPE_BACKGROUND_SIZE]: null,
            [TYPE_BACKGROUND_REPEAT]: null,
            [TYPE_BACKGROUND_LOADING_METHOD]: 'normal',
        },
        [STATE_KEY_BORDER]: borderDecoratorState = {
            [TYPE_BORDER_X]: 'none',
            [TYPE_BORDER_Y]: 'none',
            [TYPE_BORDER_COLOR]: 'rgba(0,0,0,0.1)',
            [TYPE_BORDER_WIDTH]: 1,
            [TYPE_BORDER_STYLE]: 'solid',
        },
        [STATE_KEY_EFFECTS]: effectDecoratorState = {
            [TYPE_ANIMATION_STYLE]: 'none',
            [TYPE_ANIMATION_DELAY]: 0,
            [TYPE_ANIMATION_DURATION]: 1000,
            [TYPE_ANIMATION_HOVER]: 'none',
            [TYPE_BOX_SHADOW]: false,
        },
        [STATE_KEY_POSITION]: positionDecoratorState = {
            [TYPE_PADDING]: {
                top: null,
                right: null,
                bottom: null,
                left: null,
            },
            [TYPE_MARGIN]: {
                top: null,
                right: null,
                bottom: null,
                left: null,
            },
            [TYPE_LOCKS]: {
                [TYPE_PADDING]: {
                    [DIRECTION_VERT]: false,
                    [DIRECTION_HORZ]: false,
                },
                [TYPE_MARGIN]: {
                    [DIRECTION_VERT]: false,
                    [DIRECTION_HORZ]: false,
                },
            },
            [TYPE_PADDING_MOBILE]: false,
        },
        [STATE_KEY_ANCHOR]: anchorDecoratorState = {
            [TYPE_ANCHOR_ID]: '',
        },
    } = state

    const {
        [TYPE_PADDING]: padding,
        [TYPE_PADDING_MOBILE]: paddingMobile,
        [TYPE_MARGIN]: margin,
        [TYPE_LOCKS]: locks,
    } = positionDecoratorState

    const {
        [TYPE_BACKGROUND_COLOR]: backgroundColor,
        [TYPE_BACKGROUND_COLOR_OPACITY]: backgroundColorOpacity,
        [TYPE_BACKGROUND_IMAGE]: backgroundImage,
        [TYPE_BACKGROUND_VIDEO]: backgroundVideo,
        [TYPE_BACKGROUND_POSITION]: backgroundPosition,
        [TYPE_BACKGROUND_SIZE]: backgroundSize,
        [TYPE_BACKGROUND_REPEAT]: backgroundRepeat,
        [TYPE_BACKGROUND_ALT_TEXT]: backgroundAltText,
        [TYPE_BACKGROUND_LOADING_METHOD]: backgroundLoadingMethod,
        [TYPE_BACKGROUND_MODE]: backgroundMode,
    } = backgroundDecoratorState

    const {
        [TYPE_BORDER_X]: borderX,
        [TYPE_BORDER_Y]: borderY,
        [TYPE_BORDER_COLOR]: borderColor,
        [TYPE_BORDER_WIDTH]: borderWidth,
        [TYPE_BORDER_STYLE]: borderStyle,
    } = borderDecoratorState

    const {
        [TYPE_ANIMATION_STYLE]: animationStyle,
        [TYPE_ANIMATION_DELAY]: animationDelay,
        [TYPE_ANIMATION_DURATION]: animationDuration,
        [TYPE_ANIMATION_HOVER]: animationHover,
        [TYPE_BOX_SHADOW]: boxShadow,
    } = effectDecoratorState

    const { [TYPE_ANCHOR_ID]: anchorId } = anchorDecoratorState

    const onValueChange = (type, pos) => selected => {
        let change = {
            [pos]: selected,
        }

        if (isLocked(positionDecoratorState, type, pos)) {
            const oppositePos = getOpposite(pos)

            if (oppositePos) {
                change[oppositePos] = selected
            }
        }

        onChange({
            [STATE_KEY_POSITION]: {
                ...positionDecoratorState,
                ...{
                    [type]: { ...positionDecoratorState[type], ...change },
                },
            },
        })
    }

    const onLockClick = (type, direction) => () => {
        const locked = !positionDecoratorState[TYPE_LOCKS][type][direction]
        let positionChange = {}

        if (locked) {
            const [primary, secondary] = getPositionsForDirection(direction)
            const value =
                positionDecoratorState[type][primary] || positionDecoratorState[type][secondary]

            positionChange = {
                [type]: {
                    ...positionDecoratorState[type],
                    ...{
                        [primary]: value,
                        [secondary]: value,
                    },
                },
            }
        }

        onChange({
            [STATE_KEY_POSITION]: {
                ...positionDecoratorState,
                ...{
                    [TYPE_LOCKS]: {
                        ...positionDecoratorState[TYPE_LOCKS],
                        ...{
                            [type]: {
                                ...positionDecoratorState[TYPE_LOCKS][type],
                                ...{
                                    [direction]: !positionDecoratorState[TYPE_LOCKS][type][
                                        direction
                                    ],
                                },
                            },
                        },
                    },
                },
                ...positionChange,
            },
        })
    }

    const onBackgroundChange = (value, key) => {
        onChange({
            [STATE_KEY_BACKGROUND]: {
                ...backgroundDecoratorState,
                ...{
                    [key]: value,
                },
            },
        })
    }

    const onBorderChange = (value, key) => {
        onChange({
            [STATE_KEY_BORDER]: {
                ...borderDecoratorState,
                ...{
                    [key]: value,
                },
            },
        })
    }

    const onEffectChange = (value, key) => {
        onChange({
            [STATE_KEY_EFFECTS]: {
                ...effectDecoratorState,
                ...{
                    [key]: value,
                },
            },
        })
    }

    const onAnchorChange = (value, key) => {
        onChange({
            [STATE_KEY_ANCHOR]: {
                ...anchorDecoratorState,
                ...{
                    [key]: value,
                },
            },
        })
    }

    const [internalBackgroundAltText, setInternalBackgroundAltText] = useState(
        backgroundAltText || ''
    )

    return (
        <>
            <Group title="Background">
                <Select
                    label={translations.backgroundColorInput}
                    options={[...options[TYPE_BACKGROUND_COLOR], ...themeColours]}
                    value={backgroundColor}
                    onChange={e => onBackgroundChange(e.value, TYPE_BACKGROUND_COLOR)}
                />

                {backgroundColor && (
                    <Slider
                        label={`${translations.backgroundColorOpacityInput} (${Math.floor(
                            (backgroundColorOpacity || 1) * 100
                        )}%)`}
                        min={0}
                        max={1}
                        step={0.01}
                        value={backgroundColorOpacity || 1}
                        onChange={e =>
                            onBackgroundChange(e.target.value, TYPE_BACKGROUND_COLOR_OPACITY)
                        }
                    />
                )}

                <Select
                    label={translations.backgroundModeInput}
                    options={options[TYPE_BACKGROUND_MODE]}
                    value={backgroundMode}
                    onChange={e => onBackgroundChange(e.value, TYPE_BACKGROUND_MODE)}
                />

                {backgroundMode === BACKGROUND_MODE_VIDEO_FLAG && (
                    <>
                        <File
                            label={translations.backgroundVideoInput}
                            value={backgroundVideo}
                            onChange={value => onBackgroundChange(value, TYPE_BACKGROUND_VIDEO)}
                        />

                        <File
                            label={translations.backgroundPosterframeInput}
                            value={backgroundImage}
                            onChange={value => onBackgroundChange(value, TYPE_BACKGROUND_IMAGE)}
                        />
                    </>
                )}

                {backgroundMode === BACKGROUND_MODE_IMAGE_FLAG && (
                    <>
                        <File
                            label={translations.backgroundImageInput}
                            value={backgroundImage}
                            onChange={value => onBackgroundChange(value, TYPE_BACKGROUND_IMAGE)}
                        />

                        {backgroundImage && backgroundImage.length > 0 && (
                            <>
                                <Select
                                    label={translations.backgroundPositionInput}
                                    options={options[TYPE_BACKGROUND_POSITION]}
                                    value={backgroundPosition}
                                    onChange={e =>
                                        onBackgroundChange(e.value, TYPE_BACKGROUND_POSITION)
                                    }
                                />

                                <Select
                                    label={translations.backgroundSizeInput}
                                    options={options[TYPE_BACKGROUND_SIZE]}
                                    value={backgroundSize}
                                    onChange={e =>
                                        onBackgroundChange(e.value, TYPE_BACKGROUND_SIZE)
                                    }
                                />

                                <Select
                                    label={translations.backgroundRepeatInput}
                                    options={options[TYPE_BACKGROUND_REPEAT]}
                                    value={backgroundRepeat}
                                    onChange={e =>
                                        onBackgroundChange(e.value, TYPE_BACKGROUND_REPEAT)
                                    }
                                />

                                <TextInput
                                    label={translations.backgroundAltTextInput}
                                    value={internalBackgroundAltText}
                                    onChange={e => {
                                        setInternalBackgroundAltText(e.target.value)
                                        const onChangeBackgroundAltText = onBackgroundChange(
                                            e.target.value,
                                            TYPE_BACKGROUND_ALT_TEXT
                                        )

                                        debounce(() => onChangeBackgroundAltText(), 200)
                                    }}
                                    tooltip="Adds alt text support to background images, to allow screen readers to read a description of the image's content"
                                />

                                <Select
                                    label={translations.backgroundLoadingMethodInput}
                                    options={options[TYPE_BACKGROUND_LOADING_METHOD]}
                                    value={backgroundLoadingMethod}
                                    onChange={e =>
                                        onBackgroundChange(e.value, TYPE_BACKGROUND_LOADING_METHOD)
                                    }
                                    tooltip="Preload should be used sparingly to improve performance, limit use to large above the fold images."
                                />
                            </>
                        )}
                    </>
                )}
            </Group>
            <Group title="Spacing">
                <div className="!flex !items-end">
                    <Size
                        label="Padding top"
                        options={options[TYPE_PADDING]}
                        value={padding.top}
                        onChange={onValueChange('padding', 'top')}
                    />
                    <Lock
                        locked={locks.padding.vertical}
                        onClick={onLockClick(TYPE_PADDING, DIRECTION_VERT)}
                    />
                    <Size
                        label="Padding bottom"
                        value={padding.bottom}
                        onChange={onValueChange('padding', 'bottom')}
                        options={options[TYPE_PADDING]}
                    />
                </div>
                <div className="!flex !items-end">
                    <Size
                        label="Padding left"
                        value={padding.left}
                        onChange={onValueChange('padding', 'left')}
                        options={options[TYPE_PADDING]}
                    />
                    <Lock
                        locked={locks.padding.horizontal}
                        onClick={onLockClick(TYPE_PADDING, DIRECTION_HORZ)}
                    />
                    <Size
                        label="Padding right"
                        value={padding.right}
                        onChange={onValueChange('padding', 'right')}
                        options={options[TYPE_PADDING]}
                    />
                </div>

                <Checkbox
                    label="Halve Padding on Mobile"
                    checked={paddingMobile}
                    onChange={e => {
                        onChange({
                            [STATE_KEY_POSITION]: {
                                ...positionDecoratorState,
                                [TYPE_PADDING_MOBILE]: e.target.checked,
                            },
                        })
                    }}
                />
                <hr />
                <div className="!flex !items-end">
                    <Size
                        label="Margin top"
                        value={margin.top || 0}
                        onChange={onValueChange('margin', 'top')}
                        options={[...negativeValues, ...options[TYPE_MARGIN]]}
                    />
                    <Lock
                        locked={locks.margin.vertical}
                        onClick={onLockClick(TYPE_MARGIN, DIRECTION_VERT)}
                    />
                    <Size
                        label="Margin bottom"
                        value={margin.bottom || 0}
                        onChange={onValueChange('margin', 'bottom')}
                        options={[...negativeValues, ...options[TYPE_MARGIN]]}
                    />
                </div>
                <div className="!flex !items-end">
                    <Size
                        label="Margin left"
                        value={margin.left}
                        onChange={onValueChange('margin', 'left')}
                        options={options[TYPE_MARGIN]}
                    />
                    <Lock
                        locked={locks.margin.horizontal}
                        onClick={onLockClick(TYPE_MARGIN, DIRECTION_HORZ)}
                    />
                    <Size
                        label="Margin right"
                        value={margin.right}
                        onChange={onValueChange('margin', 'right')}
                        options={options[TYPE_MARGIN]}
                    />
                </div>
            </Group>

            <Group title="Border">
                <div className="!flex !justify-between">
                    <div style={{ width: `49%` }}>
                        <Select
                            label={translations.borderXInput}
                            options={options[TYPE_BORDER_X]}
                            value={borderX}
                            onChange={e => onBorderChange(e.value, TYPE_BORDER_X)}
                        />
                    </div>

                    <div style={{ width: `49%` }}>
                        <Select
                            label={translations.borderYInput}
                            options={options[TYPE_BORDER_Y]}
                            value={borderY}
                            onChange={e => onBorderChange(e.value, TYPE_BORDER_Y)}
                        />
                    </div>
                </div>

                <Slider
                    label={`${translations.borderWidthInput} (${borderWidth}px)`}
                    min={1}
                    max={20}
                    step={1}
                    value={borderWidth}
                    onChange={e => onBorderChange(e.target.value, TYPE_BORDER_WIDTH)}
                />

                <Select
                    label={translations.borderColorInput}
                    options={[...options[TYPE_BORDER_COLOR], ...themeColours]}
                    value={borderColor}
                    onChange={e => onBorderChange(e.value, TYPE_BORDER_COLOR)}
                />

                <Select
                    label={translations.borderStyleInput}
                    options={options[TYPE_BORDER_STYLE]}
                    value={borderStyle}
                    onChange={e => onBorderChange(e.value, TYPE_BORDER_STYLE)}
                />
            </Group>

            <Group title="Effects">
                <h6>Box Shadow</h6>
                <Checkbox
                    label="Display Box Shadow"
                    checked={boxShadow}
                    onChange={e => onEffectChange(e.target.checked, TYPE_BOX_SHADOW)}
                />

                <h6>Entrance Animation</h6>
                <Select
                    label={translations.animationStyleInput}
                    options={options[TYPE_ANIMATION_STYLE]}
                    value={animationStyle}
                    onChange={e => onEffectChange(e.value, TYPE_ANIMATION_STYLE)}
                />

                <Slider
                    label={`${translations.animationDelayInput} (${animationDelay}ms)`}
                    min={0}
                    max={5000}
                    step={100}
                    value={animationDelay}
                    onChange={e => onEffectChange(e.target.value, TYPE_ANIMATION_DELAY)}
                />

                <Slider
                    label={`${translations.animationDurationInput} (${animationDuration}ms)`}
                    min={100}
                    max={5000}
                    step={100}
                    value={animationDuration}
                    onChange={e => onEffectChange(e.target.value, TYPE_ANIMATION_DURATION)}
                />

                <h6>Hover Animation</h6>

                <Select
                    label={translations.animationStyleInput}
                    options={options[TYPE_ANIMATION_HOVER]}
                    value={animationHover}
                    onChange={e => onEffectChange(e.value, TYPE_ANIMATION_HOVER)}
                />
            </Group>

            <Group title="Anchor">
                <TextInput
                    label={translations.anchorIdInput}
                    value={anchorId}
                    onChange={e => {
                        onAnchorChange(e.target.value, TYPE_ANCHOR_ID)
                    }}
                    onBlur={e => {
                        let value = e.target.value

                        if (!isNaN(parseFloat(value[0])) && isFinite(value[0])) {
                            value = `id-${value}`
                        }

                        onAnchorChange(
                            value.toLowerCase().replace(/([^A-Za-z0-9[\]{}_.:-])\s?/g, '-'),
                            TYPE_ANCHOR_ID
                        )
                    }}
                />
            </Group>

            {/* <Group title="Interactions">
                <p>Interaction Options here...</p>
            </Group> */}
        </>
    )
}

export default BlockDecoratorForm
